import React, { useState, useEffect, useCallback } from 'react';
import './PortfolioManagement.css';
import PortfolioSummary from './components/PortfolioSummary';
import FilterControls from './components/FilterControls';
import PerformanceChart from './components/PerformanceChart';
import StatisticsGrid from './components/StatisticsGrid';
import SearchControls from './components/SearchControls';
import TransactionTable from './components/TransactionTable';
import PaginationControls from './components/PaginationControls';
import { useSession } from '../../components/session/SessionManager';
import { portfolioService } from '../../services/api/portfolioService';
import { 
  Transaction, 
  PortfolioAnalytics, 
  TransactionFilters 
} from '../../types/portfolio';
import { isApiError } from '../../services/api/client';

const PortfolioManagement: React.FC = () => {
  const sessionData = useSession();
  
  // State management for components
  const [activeFilter, setActiveFilter] = useState<'7D' | '30D' | '90D' | '1Y' | 'All'>('30D');
  const [searchValue, setSearchValue] = useState('');
  const [dateValue, setDateValue] = useState('2024-12-01');
  const [currentPage, setCurrentPage] = useState(1);

  // API data state
  const [analytics, setAnalytics] = useState<PortfolioAnalytics | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter options
  const filterOptions = [
    { label: '7D', value: '7D' },
    { label: '30D', value: '30D' },
    { label: '90D', value: '90D' },
    { label: '1Y', value: '1Y' },
    { label: 'All', value: 'All' }
  ];

  // Fetch portfolio analytics
  const fetchAnalytics = useCallback(async () => {
    if (!sessionData?.currentAccount?.accountId) return;

    try {
      setLoading(true);
      const analyticsData = await portfolioService.getPortfolioAnalytics(
        sessionData.currentAccount.accountId,
        activeFilter
      );
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      if (isApiError(err)) {
        setError(`Failed to load analytics: ${err.message}`);
      } else {
        setError('Failed to load analytics. Please try again.');
      }
    }
  }, [sessionData?.currentAccount?.accountId, activeFilter]);

  // Fetch performance chart data
  const fetchPerformanceData = useCallback(async () => {
    if (!sessionData?.currentAccount?.accountId) return;

    try {
      await portfolioService.getPerformanceChartData(
        sessionData.currentAccount.accountId,
        activeFilter
      );
      // Performance data fetched but not stored for now
    } catch (err) {
      console.error('Error fetching performance data:', err);
    }
  }, [sessionData?.currentAccount?.accountId, activeFilter]);

  // Fetch transaction history
  const fetchTransactions = useCallback(async () => {
    if (!sessionData?.currentAccount?.accountId) return;

    try {
      setLoading(true);
      const filters: TransactionFilters = {
        accountId: sessionData.currentAccount.accountId,
        page: currentPage,
        limit: 10
      };

      if (searchValue) {
        // Note: API might not support search, this is a placeholder
        // filters.search = searchValue;
      }

      if (dateValue) {
        filters.from = new Date(dateValue).toISOString();
      }

      const response = await portfolioService.getTransactions(filters);
      setTransactions(response.transactions);
      setTotalTransactions(response.pagination.totalItems);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [sessionData?.currentAccount?.accountId, currentPage, searchValue, dateValue]);

  // Initial data fetch
  useEffect(() => {
    if (sessionData?.currentAccount?.accountId) {
      fetchAnalytics();
      fetchPerformanceData();
    }
  }, [fetchAnalytics, fetchPerformanceData]);

  // Separate effect for transactions that includes currentPage dependency
  useEffect(() => {
    if (sessionData?.currentAccount?.accountId) {
      fetchTransactions();
    }
  }, [fetchTransactions, currentPage]);

  // Convert analytics data to metrics format
  const metricsData = analytics ? [
    {
      label: 'Total Balance',
      value: `$${analytics.totalBalance.toLocaleString()}`,
      change: `${analytics.todayPnL >= 0 ? '+' : ''}$${analytics.todayPnL.toFixed(2)} today`,
      changeType: analytics.todayPnL >= 0 ? 'positive' as const : 'negative' as const
    },
    {
      label: 'Total P&L',
      value: `${analytics.totalPnL >= 0 ? '+' : ''}$${analytics.totalPnL.toLocaleString()}`,
      change: `${analytics.monthPnL >= 0 ? '+' : ''}$${analytics.monthPnL.toFixed(2)} this month`,
      changeType: analytics.totalPnL >= 0 ? 'positive' as const : 'negative' as const
    },
    {
      label: 'Win Rate',
      value: `${(analytics.winRate * 100).toFixed(1)}%`,
      change: `${analytics.totalTrades} total trades`,
      changeType: analytics.winRate >= 0.5 ? 'positive' as const : 'negative' as const
    },
    {
      label: 'Total Trades',
      value: analytics.totalTrades.toString(),
      change: `${analytics.weekPnL >= 0 ? '+' : ''}$${analytics.weekPnL.toFixed(2)} this week`,
      changeType: 'neutral' as const
    }
  ] : [];

  // Convert analytics data to statistics format
  const statsData = analytics ? [
    { title: 'Average Win', value: `$${analytics.averageWin.toFixed(2)}`, subtitle: 'Per winning trade' },
    { title: 'Average Loss', value: `$${analytics.averageLoss.toFixed(2)}`, subtitle: 'Per losing trade' },
    { title: 'Best Day', value: `+$${analytics.bestDay.toFixed(2)}`, subtitle: 'Best single day' },
    { title: 'Worst Day', value: `$${analytics.worstDay.toFixed(2)}`, subtitle: 'Worst single day' },
    { title: 'Profit Factor', value: analytics.profitFactor.toFixed(2), subtitle: 'Gross profit / Gross loss' },
    { title: 'Sharpe Ratio', value: analytics.sharpeRatio.toFixed(2), subtitle: 'Risk-adjusted return' }
  ] : [];

  // Convert API transactions to component format
  const formattedTransactions = transactions.map(transaction => ({
    id: transaction.transactionId,
    dateTime: new Date(transaction.timestamp).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    }),
    instrument: 'N/A', // Transactions don't have instrument info directly
    type: transaction.type as any,
    stake: `$${Math.abs(transaction.amount).toFixed(2)}`,
    status: transaction.amount >= 0 ? 'won' as const : 'lost' as const,
    pnl: `${transaction.amount >= 0 ? '+' : ''}$${transaction.amount.toFixed(2)}`,
    pnlType: transaction.amount >= 0 ? 'positive' as const : 'negative' as const,
    details: transaction.description
  }));

  // Event handlers
  const handleFilterChange = (value: string) => {
    setActiveFilter(value as '7D' | '30D' | '90D' | '1Y' | 'All');
  };

  const handleExport = async () => {
    if (!sessionData?.currentAccount?.accountId) return;

    try {
      setLoading(true);
      const filters: TransactionFilters = {
        accountId: sessionData.currentAccount.accountId,
        limit: 1000 // Get more for export
      };

      if (dateValue) {
        filters.from = new Date(dateValue).toISOString();
      }

      const csvData = await portfolioService.exportTransactions(filters);
      
      // Download CSV file
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting transactions:', err);
      setError('Failed to export transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleTransactionClick = (_transaction: any) => {
    // TODO: Show transaction details modal
  };

  if (!sessionData?.currentAccount) {
    return (
      <div className="portfolio-management">
        <div className="loading-state">
          <h2>Please select an account to view portfolio data</h2>
        </div>
      </div>
    );
  }

  if (loading && !analytics && transactions.length === 0) {
    return (
      <div className="portfolio-management">
        <div className="loading-state">
          <h2>Loading portfolio data...</h2>
        </div>
      </div>
    );
  }

  if (error && !analytics) {
    return (
      <div className="portfolio-management">
        <div className="error-state">
          <h2>Error loading portfolio data</h2>
          <p>{error}</p>
          <button onClick={() => {
            setError(null);
            fetchAnalytics();
            fetchTransactions();
          }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-management">
      {/* Portfolio Summary */}
      <PortfolioSummary metrics={metricsData} />

      {/* Content Panels */}
      <div className="content-panels">
        {/* Performance Analytics Panel */}
        <section className="analytics-panel">
          <div className="panel-header">
            <h2 className="panel-title">Performance Analytics</h2>
            <FilterControls 
              options={filterOptions}
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
            />
          </div>
          
          <div className="analytics-content">
            <PerformanceChart />
            <StatisticsGrid stats={statsData} />
          </div>
        </section>

        {/* Transaction History Panel */}
        <section className="history-panel">
          <div className="panel-header">
            <h2 className="panel-title">Transaction History</h2>
            <SearchControls
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              dateValue={dateValue}
              onDateChange={setDateValue}
              onExport={handleExport}
            />
          </div>
          
          <div className="history-content">
            <TransactionTable 
              transactions={formattedTransactions}
              onRowClick={handleTransactionClick}
            />
            <PaginationControls
              currentPage={currentPage}
              totalPages={Math.ceil(totalTransactions / 10)}
              totalItems={totalTransactions}
              itemsPerPage={10}
              onPageChange={setCurrentPage}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default PortfolioManagement;
export { PortfolioManagement };
